import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import adService from '../services/adService';
import { isWeb, isNativeMobile } from '../utils/platform';

/**
 * Ad Context - Issue #268: Optimized ad consent flow
 * - Consent stored in database, synced with userData
 * - Popover shown after 5 user-generated contents viewed
 * - Toggle available in account settings
 *
 * Issue #272: Decline count tracking
 * - Accept = never show again
 * - Decline once = show after 20 days
 * - Decline twice = never show again
 */
const AdContext = createContext(null);

// Demo mode: set to true to show custom placeholder ads instead of Google test ads
const DEMO_MODE = true;

// Number of contents to view before showing consent popover
const CONTENT_VIEW_THRESHOLD = 5;

// Days to wait before showing popover again after first decline
const DECLINE_REASK_DAYS = 20;

export const AdProvider = ({ children }) => {
  // State synced from userData (database)
  const [consentGiven, setConsentGiven] = useState(null);
  const [contentViewsCount, setContentViewsCount] = useState(0);
  const [consentAsked, setConsentAsked] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Track if popover should be shown (after 5 contents and not yet asked)
  const [showConsentPopover, setShowConsentPopover] = useState(false);

  // Issue #275: Track confirmation popover state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null); // 'positive' or 'negative'

  // Pending content views to batch update
  const pendingViewsRef = useRef(0);
  const updateTimeoutRef = useRef(null);

  // Ref to track current consentAsked state for use in async callbacks
  const consentAskedRef = useRef(consentAsked);

  // Initialize from userData when it changes
  const initFromUserData = useCallback((userData) => {
    if (!userData) return;

    const consent = userData.personalized_ads_consent;
    const views = userData.content_views_count || 0;
    const declineCount = userData.ads_consent_decline_count || 0;
    const consentUpdatedAt = userData.ads_consent_updated_at;

    setConsentGiven(consent);
    setContentViewsCount(views);

    // Issue #272: Determine if popover should be shown based on decline count
    let shouldShowPopover = false;
    let shouldTrackAsAsked = consent !== null;

    if (consent === null) {
      // Never asked before - show after 5 contents
      if (views >= CONTENT_VIEW_THRESHOLD) {
        shouldShowPopover = true;
      }
      shouldTrackAsAsked = false;
    } else if (consent === true) {
      // Accepted - never show again
      shouldShowPopover = false;
      shouldTrackAsAsked = true;
    } else if (consent === false) {
      // Declined - check decline count and time
      if (declineCount >= 2) {
        // Declined twice - never show again
        shouldShowPopover = false;
        shouldTrackAsAsked = true;
      } else if (declineCount === 1 && consentUpdatedAt) {
        // Declined once - check if 20 days have passed
        const declineDate = new Date(consentUpdatedAt);
        const now = new Date();
        const daysSinceDecline = Math.floor((now - declineDate) / (1000 * 60 * 60 * 24));

        if (daysSinceDecline >= DECLINE_REASK_DAYS) {
          // 20+ days passed - show again after content threshold
          if (views >= CONTENT_VIEW_THRESHOLD) {
            shouldShowPopover = true;
          }
          shouldTrackAsAsked = false; // Allow tracking content views again
        } else {
          // Less than 20 days - don't show
          shouldShowPopover = false;
          shouldTrackAsAsked = true;
        }
      }
    }

    setConsentAsked(shouldTrackAsAsked);
    consentAskedRef.current = shouldTrackAsAsked;
    setShowConsentPopover(shouldShowPopover);

    // Always initialize ads — consent only controls NPA mode
    if (!isInitialized) {
      const npa = consent !== true; // NPA unless explicitly accepted
      if (DEMO_MODE) {
        setIsInitialized(true);
      } else {
        adService.initialize(npa)
          .then(() => {
            setIsInitialized(true);
          })
          .catch((error) => {
            console.warn('[ADS] Failed to initialize ad service:', error);
          });
      }
    }
  }, [isInitialized]);

  // Grant consent (save to database, switch to personalized ads)
  const grantConsent = useCallback(async () => {
    try {
      await axios.post('/api/user/ads-consent', { consent: true });
      setConsentGiven(true);
      setConsentAsked(true);
      consentAskedRef.current = true;
      setShowConsentPopover(false);

      // Clear any pending content view timeout to prevent stale callbacks
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }

      // Issue #275: Show positive confirmation popover
      setConfirmationType('positive');
      setShowConfirmation(true);
    } catch (error) {
      console.error('[ADS] Failed to save consent:', error);
    }
  }, []);

  // Decline consent (save to database, keep NPA ads running)
  const declineConsent = useCallback(async () => {
    try {
      await axios.post('/api/user/ads-consent', { consent: false });
      setConsentGiven(false);
      setConsentAsked(true);
      consentAskedRef.current = true;
      setShowConsentPopover(false);

      // Clear any pending content view timeout to prevent stale callbacks
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }

      // Ensure ads are initialized in NPA mode (they should already be, but just in case)
      if (!isInitialized) {
        if (DEMO_MODE) {
          setIsInitialized(true);
        } else {
          try {
            await adService.initialize(true);
            setIsInitialized(true);
          } catch (error) {
            console.warn('[ADS] Failed to initialize ad service:', error);
          }
        }
      }

      // Issue #275: Show negative confirmation popover
      setConfirmationType('negative');
      setShowConfirmation(true);
    } catch (error) {
      console.error('[ADS] Failed to save consent decline:', error);
    }
  }, [isInitialized]);

  // Issue #275: Dismiss confirmation popover
  const dismissConfirmation = useCallback(() => {
    const wasPositive = confirmationType === 'positive';
    setShowConfirmation(false);
    setConfirmationType(null);

    // If user accepted consent on web, reload to switch to personalized ads
    if (wasPositive && isWeb()) {
      window.location.reload();
    } else if (wasPositive && isNativeMobile()) {
      // On mobile, reinitialize AdMob with personalized ads
      if (!DEMO_MODE) {
        adService.initialize(false)
          .then(() => setIsInitialized(true))
          .catch((error) => console.warn('[ADS] Failed to reinitialize ad service:', error));
      }
    }
  }, [confirmationType]);

  // Revoke consent (used from settings toggle — switch to NPA, ads stay visible)
  const revokeConsent = useCallback(async () => {
    try {
      await axios.post('/api/user/ads-consent', { consent: false });
      setConsentGiven(false);

      // On web, reload to switch AdSense to NPA mode
      if (isWeb()) {
        window.location.reload();
      }
      // On mobile, AdMob can be reinitialized without reload (handled on next init)
    } catch (error) {
      console.error('[ADS] Failed to revoke consent:', error);
    }
  }, []);

  // Issue #268: Track content view and trigger popover after threshold
  const trackContentView = useCallback(() => {
    // Don't track if consent already asked
    if (consentAsked) return;

    // Batch updates to reduce API calls
    pendingViewsRef.current += 1;

    // Debounce the API call
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(async () => {
      const viewsToAdd = pendingViewsRef.current;
      pendingViewsRef.current = 0;

      try {
        const response = await axios.post('/api/user/content-views', { count: viewsToAdd });
        const newCount = response.data.content_views_count;
        setContentViewsCount(newCount);

        // Show popover after threshold reached (use ref to get current value)
        if (newCount >= CONTENT_VIEW_THRESHOLD && !consentAskedRef.current) {
          setShowConsentPopover(true);
        }
      } catch (error) {
        console.error('[ADS] Failed to track content view:', error);
      }
    }, 1000); // 1 second debounce
  }, [consentAsked]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  return (
    <AdContext.Provider value={{
      consentGiven,
      consentAsked,
      isInitialized,
      demoMode: DEMO_MODE,
      showConsentPopover,
      contentViewsCount,
      grantConsent,
      revokeConsent,
      declineConsent,
      trackContentView,
      initFromUserData,
      setShowConsentPopover,
      // Issue #275: Confirmation popover state
      showConfirmation,
      confirmationType,
      dismissConfirmation,
    }}>
      {children}
    </AdContext.Provider>
  );
};

export const useAds = () => {
  const context = useContext(AdContext);
  if (!context) {
    throw new Error('useAds must be used within an AdProvider');
  }
  return context;
};

export default AdContext;
